import { useState } from 'react';

import { DataGrid } from '@/components';
import { useDataGridColumns } from '@/components/DataGrid/hooks/useDataGridCols';
import {
  DataGridColSettings,
  mergeColumnSettings,
} from '@/components/DataGrid/utils';
import { LocalStoragePrefix } from '@/constants';
import { AuditTrailTableColumns } from '@/pages/Tools/InternalAffairs/data';
import { auditTrailDummyData } from '@/pages/Tools/InternalAffairs/dummyData';
import { useUserStore } from '@/store';
import { getLocalStorageItem } from '@/utils';

interface InternalAffairsAuditTrailProps {
  caseId?: string;
}

export const InternalAffairsAuditTrail = ({
  caseId,
}: InternalAffairsAuditTrailProps) => {
  const { user } = useUserStore();

  // Filter audit trail by caseId if provided
  const [auditTrail] = useState(() => {
    if (caseId) {
      return auditTrailDummyData.filter((item) => item.iaCaseId === caseId);
    }
    return auditTrailDummyData;
  });

  const [refreshKey] = useState(0);

  const [columnSettings, setColumnSettings] = useState<DataGridColSettings[]>(
    () => {
      const savedSettings = getLocalStorageItem({
        key: String(user?._id),
        prefix: `auditTrail-${LocalStoragePrefix.TableSetting}`,
        shouldParse: true,
      });

      if (!savedSettings) {
        return AuditTrailTableColumns;
      }

      return mergeColumnSettings(AuditTrailTableColumns, savedSettings);
    },
  );

  const columns = useDataGridColumns({
    type: 'auditTrail',
    columnSettings,
  });

  // Updated fetchAuditTrail to match expected signature and use actual properties
  const fetchAuditTrail = async (
    _page: number,
    _pageSize: number,
    filter: string,
    sortParam?: string,
  ) => {
    let filteredAuditTrail = [...auditTrail];

    // Parse and apply filters if they exist
    if (filter) {
      try {
        const filterModel = JSON.parse(filter);

        // Handle quick filter (search bar)
        if (
          filterModel.quickFilterValues &&
          filterModel.quickFilterValues.length > 0
        ) {
          const searchTerm = filterModel.quickFilterValues
            .join(' ')
            .toLowerCase();

          filteredAuditTrail = filteredAuditTrail.filter((item) => {
            // Search across actual audit trail fields that exist in the type
            return (
              item.event?.toLowerCase().includes(searchTerm) ||
              item.field?.toLowerCase().includes(searchTerm) ||
              item.value?.toLowerCase().includes(searchTerm) ||
              item.changedBy?.toLowerCase().includes(searchTerm) ||
              item.triggeredBy?.toLowerCase().includes(searchTerm) ||
              item.createdAt?.toLowerCase().includes(searchTerm) ||
              item.iaCaseId?.toLowerCase().includes(searchTerm) ||
              // Search in metadata if needed
              item.metadata?.caseType?.toLowerCase().includes(searchTerm) ||
              item.metadata?.priority?.toLowerCase().includes(searchTerm)
            );
          });
        }

        // Handle column filters
        if (filterModel.items && filterModel.items.length > 0) {
          filterModel.items.forEach((filterItem: any) => {
            if (
              filterItem.value !== undefined &&
              filterItem.value !== null &&
              filterItem.value !== ''
            ) {
              const { field, operator, value } = filterItem;

              filteredAuditTrail = filteredAuditTrail.filter((auditItem) => {
                // Type-safe field access
                const fieldValue = (auditItem as any)[field];

                if (fieldValue === undefined || fieldValue === null)
                  return false;

                const fieldStr = String(fieldValue).toLowerCase();
                const valueStr = String(value).toLowerCase();

                switch (operator) {
                  case 'contains':
                    return fieldStr.includes(valueStr);
                  case 'equals':
                    return fieldStr === valueStr;
                  case 'startsWith':
                    return fieldStr.startsWith(valueStr);
                  case 'endsWith':
                    return fieldStr.endsWith(valueStr);
                  case 'isEmpty':
                    return !fieldValue || fieldStr === '';
                  case 'isNotEmpty':
                    return fieldValue && fieldStr !== '';
                  default:
                    return true;
                }
              });
            }
          });
        }
      } catch (e) {
        console.error('Error parsing filter:', e);
      }
    }

    // Apply sorting
    if (sortParam) {
      try {
        const sortObj = JSON.parse(sortParam);
        const sortEntries = Object.entries(sortObj);

        if (sortEntries.length > 0) {
          const [field, direction] = sortEntries[0];

          filteredAuditTrail.sort((a, b) => {
            const aVal = (a as any)[field];
            const bVal = (b as any)[field];

            if (aVal === bVal) return 0;

            const comparison = aVal > bVal ? 1 : -1;
            return direction === 1 ? comparison : -comparison;
          });
        }
      } catch (e) {
        console.error('Error parsing sort:', e);
      }
    }

    // For client-side pagination, return all filtered results
    return {
      rows: filteredAuditTrail,
      totalCount: filteredAuditTrail.length,
      hasNextPage: false,
    };
  };

  return (
    <>
      <div className="pt-3">
        <DataGrid
          className="w-full"
          key={refreshKey}
          name="auditTrail"
          autoPageSize={false}
          fetchData={fetchAuditTrail}
          columns={columns}
          columnSettings={columnSettings}
          sx={{
            width: 'calc(100% - 20px)',
          }}
          disableMultipleColumnsSorting={true}
          setColumnSettings={setColumnSettings}
          getRowId={(row) => row?.id || ''}
          hideQuickFilter={true}
        />
      </div>
    </>
  );
};
